import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import CompassIcon from "../../assets/CompassIconPlain.png";
import "./Login.css";

const resetPasswordSchema = yup.object({
  email: yup
    .string("Enter your email.")
    .email("Enter a valid email.")
    .required("Email is required."),
  confirmationCode: yup
    .string()
    .matches(
      /(?=.*?\d)^\$?(([1-9]\d{0,2}(,\d{3})*)|\d+)?(\.\d{1,2})?$/,
      "Only numbers are allowed.",
    )
    .required("Confirmation code is required."),
  newPassword: yup
    .string("Enter your password.")
    .min(8, "Password should be of minimum 8 characters length.")
    .required("Password is required."),
});

require("dotenv").config();
const BASE_URL = (
  process.env.REACT_APP_BASE_URL || "http://localhost:5001/api/v1"
).replace("v1", "v2");

function ResetPasswordPage() {
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const formik = useFormik({
    initialValues: {
      email: "",
      confirmationCode: "",
      newPassword: "",
    },
    validationSchema: resetPasswordSchema,
    onSubmit: async (values) => {
      if (isSubmitting) return;
      setIsSubmitting(true);
      try {
        const response = await fetch(`${BASE_URL}/users/reset-password`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: values.email,
            confirmationCode: values.confirmationCode.toString(),
            newPassword: values.newPassword,
          }),
        });

        const resp = await response.json();

        if (response.ok) {
          toast.success(resp.message);
          setTimeout(() => {
            navigate(`/login`);
          }, 1000);
        } else {
          toast.error(resp.message || resp.error || "Password reset failed.");
        }
      } catch (error) {
        toast.error(error.message);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useEffect(() => {
    formik.setFieldValue("email", searchParams.get("email") ?? "");
    formik.setFieldValue("confirmationCode", searchParams.get("code") ?? "");
    if (location.state && location.state.email) {
      formik.setFieldValue("email", location.state.email);
    }
  }, []);

  const resendCode = async () => {
    if (isResending) return;
    setIsResending(true);
    try {
      const response = await fetch(`${BASE_URL}/users/forgot-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formik.values.email,
        }),
      });

      const resp = await response.json();

      if (response.ok) {
        toast.success(resp.message);
      } else {
        toast.info(resp.message);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsResending(false);
    }
  };

  const handleClickShowNewPassword = () => setShowNewPassword(!showNewPassword);
  const handleMouseDownNewPassword = () => setShowNewPassword(!showNewPassword);

  return (
    <div className="App-content-center App-content-bg-custom">
      <div className="loginContainerUnique">
        <div className="loginLogoUnique">
          <img src={CompassIcon} alt="Compass Care by Gray Matters Alliance" />
          <h1 style={{ marginBottom: "24px", marginTop: "24px" }}>
            Reset your Password
          </h1>
          <p></p>
        </div>
        <div>
          <Box
            component="form"
            sx={{ "& > :not(style)": { m: 1, width: "25ch" } }}
            noValidate
            autoComplete="off"
            onSubmit={formik.handleSubmit}
          >
            <div>
              <TextField
                id="email"
                label="Email"
                type="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                required
                readOnly={location.state && location.state.email}
                disabled={location.state && location.state.email}
                style={{ width: "100%" }}
              />

              <Stack>
                <small className="smallText">
                  (Please check your email for confirmation code.)
                </small>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => resendCode()}
                  disabled={isResending}
                >
                  <Typography textTransform={"capitalize"}>
                    {isResending ? "Sending..." : "Resend Code"}
                  </Typography>
                </Button>
              </Stack>

              <TextField
                id="confirmationCode"
                label="Confirmation Code"
                type="text"
                value={formik.values.confirmationCode}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.confirmationCode &&
                  Boolean(formik.errors.confirmationCode)
                }
                helperText={
                  formik.touched.confirmationCode &&
                  formik.errors.confirmationCode
                }
                required
                style={{ width: "100%" }}
              />

              <TextField
                id="newPassword"
                label="New Password"
                type={showNewPassword ? "text" : "password"}
                value={formik.values.newPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.newPassword &&
                  Boolean(formik.errors.newPassword)
                }
                helperText={
                  formik.touched.newPassword && formik.errors.newPassword
                }
                required
                style={{
                  width: "100%",
                  marginBottom: "24px",
                  marginTop: "24px",
                }}
                autoComplete="false"
                InputProps={{
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      onClick={handleClickShowNewPassword}
                      onMouseDown={handleMouseDownNewPassword}
                    >
                      {showNewPassword ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </InputAdornment>
                  ),
                }}
              />

              <button
                type="submit"
                className="btn-primary loginButtonUnique"
                style={{ width: "100%" }}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Resetting..." : "Continue"}
              </button>
            </div>

            <a className="forgotLink" href="/login">
              Back to Login
            </a>
          </Box>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
